
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Pencil, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

type AcademicYearListProps = {
  academicYears: any[];
  onSetActive: (yearId: string) => void;
  onSetActiveTerm: (termId: string) => void;
  onEdit: (year: any) => void;
  onDelete: (yearId: string) => void;
};

export function AcademicYearList({ 
  academicYears, 
  onSetActive, 
  onSetActiveTerm,
  onEdit,
  onDelete
}: AcademicYearListProps) {
  const [selectedTerm, setSelectedTerm] = useState<{id: string, name: string} | null>(null);
  const { toast } = useToast();

  // Sort academic years with active year first, then by name in descending order
  const sortedAcademicYears = [...academicYears].sort((a, b) => {
    if (a.is_active && !b.is_active) return -1;
    if (!a.is_active && b.is_active) return 1;
    return b.name.localeCompare(a.name);
  });

  const handleTermClick = (term: any, yearActive: boolean) => {
    if (!yearActive) {
      toast({
        title: "Cannot activate term",
        description: "You can only activate terms from the active academic year",
        variant: "destructive",
      });
      return;
    }
    if (!term.is_active) {
      setSelectedTerm({ id: term.id, name: term.name });
    }
  };

  const handleConfirmTermActivation = () => {
    if (selectedTerm) {
      onSetActiveTerm(selectedTerm.id);
      setSelectedTerm(null);
    }
  };

  const handleSetActive = async (yearId: string) => {
    try {
      await onSetActive(yearId);
      toast({
        title: "Success",
        description: "Academic year activated successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to activate academic year",
        variant: "destructive",
      });
    }
  };

  // Helper function to sort terms by their term number
  const sortTerms = (terms: any[]) => {
    return [...terms].sort((a, b) => {
      const termNumberA = parseInt(a.name.split(' ')[1]);
      const termNumberB = parseInt(b.name.split(' ')[1]);
      return termNumberA - termNumberB;
    });
  };

  // Format date to local format (MM/DD/YYYY)
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  return (
    <div className="rounded-lg border bg-card">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Academic Year</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Terms</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedAcademicYears?.map((year) => (
            <TableRow key={year.id}>
              <TableCell className="font-medium">{year.name}</TableCell>
              <TableCell>
                <Badge variant={year.is_active ? "default" : "secondary"}>
                  {year.is_active ? "Active" : "Inactive"}
                </Badge>
              </TableCell>
              <TableCell>
                <div className="flex flex-col gap-2">
                  {sortTerms(year.terms)?.map((term: any) => (
                    <div key={term.id} className="flex items-center gap-2">
                      <Badge 
                        variant={term.is_active ? "default" : "secondary"} 
                        className={year.is_active ? "cursor-pointer" : "cursor-not-allowed opacity-50"}
                        onClick={() => handleTermClick(term, year.is_active)}
                      >
                        {term.name}
                      </Badge>
                      <span className="text-sm text-muted-foreground">
                        {formatDate(term.start_date)} - {formatDate(term.end_date)}
                      </span>
                    </div>
                  ))}
                </div>
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-2">
                  {!year.is_active && (
                    <Button
                      variant="ghost"
                      onClick={() => handleSetActive(year.id)}
                    >
                      Set Active
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onEdit(year)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        disabled={year.is_active}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Academic Year</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to delete {year.name}? This action cannot be undone.
                          All associated terms will also be deleted.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => onDelete(year.id)}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <AlertDialog open={!!selectedTerm} onOpenChange={(open) => !open && setSelectedTerm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change Active Term</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to set {selectedTerm?.name} as the active term? 
              This will deactivate the current active term.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmTermActivation}>
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
